import { AntRole } from '@antification/core';

/**
 * Quantized angle steps for sprite caching.
 * 16 angles = 22.5° per step, good balance between smoothness and memory.
 */
export const ANGLE_STEPS = 16;
export const ANGLE_STEP_SIZE = (Math.PI * 2) / ANGLE_STEPS;

/**
 * Stats for debugging sprite cache performance.
 */
export interface SpriteCacheStats {
  initialized: boolean;
  formicariumSprites: number;
  surfaceSprites: number;
  formicariumHits: number;
  formicariumMisses: number;
  surfaceHits: number;
  surfaceMisses: number;
  lastResetTime: number;
}

/**
 * Pre-rendered ant sprites for different roles and angles.
 * Dramatically reduces draw calls from ~10 per ant to 1.
 */
export class AntSpriteCache {
  private formicarium: Map<string, HTMLCanvasElement> = new Map();
  private surface: Map<string, HTMLCanvasElement> = new Map();
  private initialized = false;

  // Stats for debugging
  private formicariumHits = 0;
  private formicariumMisses = 0;
  private surfaceHits = 0;
  private surfaceMisses = 0;
  private lastResetTime = 0;

  // Formicarium ant base size (matches AntRenderer: size = 4 * ant.size where ant.size ~= 1)
  private readonly formicariumSize = 4;
  // Render formicarium sprites at higher resolution for detail when zoomed in
  private readonly formicariumRefScale = 4;
  // Surface ant base size (matches ParkingtonLayer)
  private readonly surfaceSize = 1;

  // Color definitions matching existing renderers
  private readonly roleColors: Record<string, { color: string; darkerColor: string }> = {
    [AntRole.QUEEN]: { color: '#b8860b', darkerColor: '#8b6508' },
    [AntRole.FORAGER]: { color: '#8b6914', darkerColor: '#5a4510' },
    [AntRole.SOLDIER]: { color: '#654321', darkerColor: '#4a3018' },
    [AntRole.NURSE]: { color: '#7a6520', darkerColor: '#5a4510' },
    [AntRole.ENGINEER]: { color: '#6b5a4a', darkerColor: '#4a3a2a' },
    selected: { color: '#ffd700', darkerColor: '#b8960b' },
    hovered: { color: '#daa520', darkerColor: '#a07810' },
  };

  private readonly surfaceColonyColors = [
    { body: '#2a1810', outline: '#1a0d08' },   // Brown colony
    { body: '#1a2020', outline: '#0a1010' },   // Dark/green colony
    { body: '#3a1010', outline: '#200808' },   // Red colony
  ];

  /**
   * Initialize sprite cache. Call once before rendering.
   */
  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.lastResetTime = performance.now();

    console.log('[AntSpriteCache] Initializing sprite cache...');

    // Generate formicarium ant sprites
    for (const [roleKey, colors] of Object.entries(this.roleColors)) {
      for (let angleIdx = 0; angleIdx < ANGLE_STEPS; angleIdx++) {
        const angle = angleIdx * ANGLE_STEP_SIZE;
        const key = this.getFormicariumKey(roleKey, angleIdx);
        this.formicarium.set(key, this.renderFormicariumAnt(colors, angle));
      }
    }

    // Generate surface ant sprites for each colony
    for (let colonyIdx = 0; colonyIdx < this.surfaceColonyColors.length; colonyIdx++) {
      const colors = this.surfaceColonyColors[colonyIdx];
      for (let angleIdx = 0; angleIdx < ANGLE_STEPS; angleIdx++) {
        const angle = angleIdx * ANGLE_STEP_SIZE;
        const key = this.getSurfaceKey(colonyIdx, angleIdx);
        this.surface.set(key, this.renderSurfaceAnt(colors, angle));
      }
    }

    console.log(`[AntSpriteCache] Initialized: ${this.formicarium.size} formicarium sprites, ${this.surface.size} surface sprites`);

    // Expose stats globally for debugging
    (window as any).antSpriteCacheStats = () => this.getStats();
    (window as any).antSpriteCache = this;
  }

  /**
   * Get stats for debugging.
   */
  getStats(): SpriteCacheStats {
    return {
      initialized: this.initialized,
      formicariumSprites: this.formicarium.size,
      surfaceSprites: this.surface.size,
      formicariumHits: this.formicariumHits,
      formicariumMisses: this.formicariumMisses,
      surfaceHits: this.surfaceHits,
      surfaceMisses: this.surfaceMisses,
      lastResetTime: this.lastResetTime,
    };
  }

  /**
   * Reset hit/miss counters.
   */
  resetStats(): void {
    this.formicariumHits = 0;
    this.formicariumMisses = 0;
    this.surfaceHits = 0;
    this.surfaceMisses = 0;
    this.lastResetTime = performance.now();
  }

  /**
   * Log current stats to console.
   */
  logStats(): void {
    const stats = this.getStats();
    const elapsed = (performance.now() - stats.lastResetTime) / 1000;
    const formicariumTotal = stats.formicariumHits + stats.formicariumMisses;
    const surfaceTotal = stats.surfaceHits + stats.surfaceMisses;

    console.log(`[AntSpriteCache] Stats over ${elapsed.toFixed(1)}s:`);
    console.log(`  Formicarium: ${stats.formicariumHits} hits, ${stats.formicariumMisses} misses (${formicariumTotal > 0 ? ((stats.formicariumHits / formicariumTotal) * 100).toFixed(1) : 0}% hit rate)`);
    console.log(`  Surface: ${stats.surfaceHits} hits, ${stats.surfaceMisses} misses (${surfaceTotal > 0 ? ((stats.surfaceHits / surfaceTotal) * 100).toFixed(1) : 0}% hit rate)`);
    console.log(`  Sprites cached: ${stats.formicariumSprites} formicarium, ${stats.surfaceSprites} surface`);
  }

  /**
   * Get pre-rendered sprite for a formicarium ant.
   */
  getFormicariumSprite(
    role: AntRole,
    angle: number,
    isSelected: boolean,
    isHovered: boolean
  ): { canvas: HTMLCanvasElement; angleIdx: number } | null {
    if (!this.initialized) {
      this.formicariumMisses++;
      return null;
    }

    const roleKey = isSelected ? 'selected' : isHovered ? 'hovered' : role;
    const angleIdx = this.quantizeAngle(angle);
    const key = this.getFormicariumKey(roleKey, angleIdx);
    const canvas = this.formicarium.get(key);

    if (canvas) {
      this.formicariumHits++;
      return { canvas, angleIdx };
    } else {
      this.formicariumMisses++;
      console.warn(`[AntSpriteCache] Miss for formicarium key: ${key}`);
      return null;
    }
  }

  /**
   * Get pre-rendered sprite for a surface ant.
   */
  getSurfaceSprite(
    colonyIndex: number,
    angle: number
  ): { canvas: HTMLCanvasElement; angleIdx: number } | null {
    if (!this.initialized) {
      this.surfaceMisses++;
      return null;
    }

    const safeColonyIdx = Math.min(colonyIndex, this.surfaceColonyColors.length - 1);
    const angleIdx = this.quantizeAngle(angle);
    const key = this.getSurfaceKey(safeColonyIdx, angleIdx);
    const canvas = this.surface.get(key);

    if (canvas) {
      this.surfaceHits++;
      return { canvas, angleIdx };
    } else {
      this.surfaceMisses++;
      console.warn(`[AntSpriteCache] Miss for surface key: ${key}`);
      return null;
    }
  }

  /**
   * Get the quantized angle and sprite dimensions for positioning.
   */
  getFormicariumDimensions(): { width: number; height: number; centerX: number; centerY: number } {
    const size = this.formicariumSize;
    // Sprite needs to fit: body segments + legs + antennae
    const padding = size * 2.5;
    const width = size * 4 + padding * 2;
    const height = size * 3 + padding * 2;
    // Returns world-space dimensions (not canvas pixels which are formicariumRefScale times larger)
    return { width, height, centerX: width / 2, centerY: height / 2 };
  }

  getSurfaceDimensions(scale: number): { width: number; height: number; centerX: number; centerY: number } {
    const size = this.surfaceSize * scale;
    const padding = size * 2;
    const width = size * 4 + padding * 2;
    const height = size * 3 + padding * 2;
    return { width, height, centerX: width / 2, centerY: height / 2 };
  }

  private getFormicariumKey(roleKey: string, angleIdx: number): string {
    return `f_${roleKey}_${angleIdx}`;
  }

  private getSurfaceKey(colonyIdx: number, angleIdx: number): string {
    return `s_${colonyIdx}_${angleIdx}`;
  }

  private quantizeAngle(angle: number): number {
    // Normalize to [0, 2π)
    let normalized = angle % (Math.PI * 2);
    if (normalized < 0) normalized += Math.PI * 2;
    // Round to nearest angle step
    return Math.round(normalized / ANGLE_STEP_SIZE) % ANGLE_STEPS;
  }

  /**
   * Render a formicarium ant sprite at the given angle.
   */
  private renderFormicariumAnt(
    colors: { color: string; darkerColor: string },
    angle: number
  ): HTMLCanvasElement {
    const rs = this.formicariumRefScale;
    const size = this.formicariumSize * rs;
    const dim = this.getFormicariumDimensions();

    const canvas = document.createElement('canvas');
    canvas.width = dim.width * rs;
    canvas.height = dim.height * rs;
    const ctx = canvas.getContext('2d')!;

    const x = dim.centerX * rs;
    const y = dim.centerY * rs;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    ctx.fillStyle = colors.color;
    ctx.strokeStyle = colors.darkerColor;
    ctx.lineWidth = 0.5 * rs;

    // Abdomen (rear, largest)
    const abdomenX = x - cos * size * 1.3;
    const abdomenY = y - sin * size * 1.3;
    ctx.beginPath();
    ctx.ellipse(abdomenX, abdomenY, size * 1.2, size * 0.8, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Thorax (middle)
    ctx.beginPath();
    ctx.ellipse(x, y, size * 0.8, size * 0.55, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Head (front, smallest)
    const headX = x + cos * size * 1.1;
    const headY = y + sin * size * 1.1;
    ctx.beginPath();
    ctx.ellipse(headX, headY, size * 0.55, size * 0.45, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Antennae
    ctx.strokeStyle = colors.color;
    ctx.lineWidth = 0.7 * rs;
    const antennaLen = size * 1.0;
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle - 0.6) * antennaLen, headY + Math.sin(angle - 0.6) * antennaLen);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle + 0.6) * antennaLen, headY + Math.sin(angle + 0.6) * antennaLen);
    ctx.stroke();

    // Legs (3 pairs)
    ctx.strokeStyle = colors.darkerColor;
    ctx.lineWidth = 0.5 * rs;
    const legLen = size * 0.9;
    for (let i = -1; i <= 1; i++) {
      const legBaseX = x + cos * size * i * 0.4;
      const legBaseY = y + sin * size * i * 0.4;
      // Left leg
      const leftAngle = angle - Math.PI / 2 - 0.3 + i * 0.2;
      ctx.beginPath();
      ctx.moveTo(legBaseX, legBaseY);
      ctx.lineTo(legBaseX + Math.cos(leftAngle) * legLen, legBaseY + Math.sin(leftAngle) * legLen);
      ctx.stroke();
      // Right leg
      const rightAngle = angle + Math.PI / 2 + 0.3 - i * 0.2;
      ctx.beginPath();
      ctx.moveTo(legBaseX, legBaseY);
      ctx.lineTo(legBaseX + Math.cos(rightAngle) * legLen, legBaseY + Math.sin(rightAngle) * legLen);
      ctx.stroke();
    }

    return canvas;
  }

  /**
   * Render a surface ant sprite at the given angle.
   * Surface ants are rendered at a fixed scale, then scaled when drawn.
   */
  private renderSurfaceAnt(
    colors: { body: string; outline: string },
    angle: number
  ): HTMLCanvasElement {
    // Render at a reference scale for quality, will be scaled when drawn
    const refScale = 10; // Render at 10x for crisp downscaling
    const size = this.surfaceSize * refScale;

    const padding = size * 2;
    const width = size * 4 + padding * 2;
    const height = size * 3 + padding * 2;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d')!;

    const x = width / 2;
    const y = height / 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    ctx.fillStyle = colors.body;
    ctx.strokeStyle = colors.outline;
    ctx.lineWidth = 0.5 * refScale;

    // Abdomen (rear, largest)
    const abdomenX = x - cos * size * 1.2;
    const abdomenY = y - sin * size * 1.2;
    ctx.beginPath();
    ctx.ellipse(abdomenX, abdomenY, size * 1.1, size * 0.7, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Thorax (middle)
    ctx.beginPath();
    ctx.ellipse(x, y, size * 0.7, size * 0.5, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Head (front, smallest)
    const headX = x + cos * size * 1.0;
    const headY = y + sin * size * 1.0;
    ctx.beginPath();
    ctx.ellipse(headX, headY, size * 0.5, size * 0.4, angle, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Antennae
    ctx.strokeStyle = colors.body;
    ctx.lineWidth = 0.5 * refScale;
    const antennaLen = size * 0.8;
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle - 0.5) * antennaLen, headY + Math.sin(angle - 0.5) * antennaLen);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(headX, headY);
    ctx.lineTo(headX + Math.cos(angle + 0.5) * antennaLen, headY + Math.sin(angle + 0.5) * antennaLen);
    ctx.stroke();

    return canvas;
  }
}

// Singleton instance
export const antSpriteCache = new AntSpriteCache();
